/**
 * Aplicações (C1): com quais máquinas/modelos cada produto é compatível.
 *
 * Contrato `product_applications` (migration 0052): produto + marca + modelo, faixa de ano
 * OPCIONAL (máquina de laser costuma ter geração, não ano) e uma observação. A mesma máquina
 * não entra duas vezes para o mesmo produto (o servidor responde 409).
 *
 * A view é pura (props); o container liga o contrato HTTP. Rota ausente (404/501) vira
 * "aguardando contrato" — nunca uma lista vazia que pareça "nenhum produto tem aplicação".
 */
// Import explícito de `React` — mesmo motivo de `RmaDetalhe.tsx` (JSX clássico nos testes SSR).
import React, { useState, type ReactElement } from "react";
import { Trash2, Wrench } from "lucide-react";
import { CatalogBoundary } from "../components/CatalogBoundary.js";
import { StatusPanel, errorDetail } from "../components/StatusPanel.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import { applicationYears, type ProductApplication } from "../contracts/platform-extras.js";
import { useApplicationActions, useApplications } from "../queries.js";
import { Button, Card, CardHeader, EmptyState, Field, PageHeader, controlClass } from "../ui/index.js";
import type { CatalogDataset } from "../catalog/dataset.js";

export interface AplicacoesViewProps {
  /** `null` = rota de aplicações não publicada neste ambiente. */
  applications: ProductApplication[] | null;
  products: Array<{ productId: string; name: string; sku: string }>;
  onAdd?: (input: { productId: string; brand: string; model: string; yearFrom?: number; yearTo?: number; note?: string }) => void;
  onRemove?: (id: string) => void;
  busy?: boolean;
  error?: string | null;
}

export function AplicacoesView({ applications, products, onAdd, onRemove, busy = false, error = null }: AplicacoesViewProps): ReactElement {
  const [productId, setProductId] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");
  const [note, setNote] = useState("");
  const header = <PageHeader title="Aplicações" description="Com quais máquinas e modelos cada produto é compatível." />;

  if (applications === null) {
    return (
      <div className="page page--aplicacoes flex flex-col gap-6" data-page="aplicacoes">
        {header}
        <StatusPanel
          kind="awaiting_source"
          title={`Cadastro de aplicações ${AWAITING_CONTRACT_LABEL}`}
          detail="A rota /api/platform/catalog/applications não respondeu neste ambiente."
        />
      </div>
    );
  }

  const productName = new Map(products.map((product) => [product.productId, product]));
  const years = (value: string): number | undefined => (/^\d{4}$/.test(value) ? Number(value) : undefined);
  const valid = productId !== "" && brand.trim() !== "" && model.trim() !== "" && (yearFrom === "" || years(yearFrom) !== undefined) && (yearTo === "" || years(yearTo) !== undefined);
  const sorted = [...applications].sort((a, b) => (productName.get(a.productId)?.name ?? a.productId).localeCompare(productName.get(b.productId)?.name ?? b.productId) || a.model.localeCompare(b.model));

  return (
    <div className="page page--aplicacoes flex flex-col gap-6" data-page="aplicacoes" data-applications-count={applications.length}>
      {header}
      {onAdd ? (
        <Card data-panel="nova-aplicacao">
          <CardHeader title="Nova aplicação" description="Marca e modelo são obrigatórios; o ano é opcional." />
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            <Field label="Produto">
              {(ids: { id: string }) => (
                <select id={ids.id} className={controlClass} value={productId} data-field="produto" onChange={(event) => setProductId(event.target.value)}>
                  <option value="">Escolha um produto</option>
                  {products.map((product) => (
                    <option key={product.productId} value={product.productId}>{`${product.name} (${product.sku})`}</option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Marca da máquina">
              {(ids: { id: string }) => <input id={ids.id} className={controlClass} maxLength={60} value={brand} data-field="marca" onChange={(event) => setBrand(event.target.value)} />}
            </Field>
            <Field label="Modelo">
              {(ids: { id: string }) => <input id={ids.id} className={controlClass} maxLength={80} value={model} data-field="modelo" onChange={(event) => setModel(event.target.value)} />}
            </Field>
            <Field label="Ano inicial (opcional)">
              {(ids: { id: string }) => <input id={ids.id} className={controlClass} inputMode="numeric" maxLength={4} value={yearFrom} data-field="ano-inicial" onChange={(event) => setYearFrom(event.target.value)} />}
            </Field>
            <Field label="Ano final (opcional)">
              {(ids: { id: string }) => <input id={ids.id} className={controlClass} inputMode="numeric" maxLength={4} value={yearTo} data-field="ano-final" onChange={(event) => setYearTo(event.target.value)} />}
            </Field>
            <Field label="Observação (opcional)">
              {(ids: { id: string }) => <input id={ids.id} className={controlClass} maxLength={300} value={note} data-field="observacao" onChange={(event) => setNote(event.target.value)} />}
            </Field>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              disabled={busy || !valid}
              data-action="adicionar-aplicacao"
              onClick={() => {
                onAdd({ productId, brand: brand.trim(), model: model.trim(), yearFrom: years(yearFrom), yearTo: years(yearTo), note: note.trim() || undefined });
                setBrand("");
                setModel("");
                setYearFrom("");
                setYearTo("");
                setNote("");
              }}
            >
              {busy ? "Salvando…" : "Adicionar aplicação"}
            </Button>
            {error ? <p role="alert" className="m-0 text-sm font-semibold text-danger">{error}</p> : null}
          </div>
        </Card>
      ) : null}
      {sorted.length === 0 ? (
        <EmptyState icon={Wrench} title="Nenhuma aplicação cadastrada" description="Cadastre a primeira compatibilidade acima. Produto sem aplicação não aparece como compatível com nenhuma máquina." />
      ) : (
        <Card className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-table="aplicacoes">
              <caption className="sr-only">Compatibilidade dos produtos com máquinas e modelos</caption>
              <thead>
                <tr className="border-b border-border-strong bg-surface-soft text-xs uppercase tracking-wide text-muted">
                  {["Produto", "Máquina", "Anos", "Observação", ""].map((title) => (
                    <th key={title || "acoes"} scope="col" className="whitespace-nowrap px-4 py-3 font-bold">{title || <span className="sr-only">Ações</span>}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((application) => {
                  const product = productName.get(application.productId);
                  return (
                    <tr key={application.id} data-record-id={application.id} className="border-b border-border last:border-b-0 hover:bg-surface-soft">
                      <td className="px-4 py-3">
                        <div className="font-bold text-ink">{product?.name ?? application.productId}</div>
                        {product ? <div className="text-xs text-muted">{`SKU ${product.sku}`}</div> : null}
                      </td>
                      <td className="px-4 py-3">{`${application.brand} ${application.model}`}</td>
                      <td className="px-4 py-3">{applicationYears(application) ?? "—"}</td>
                      <td className="px-4 py-3 text-muted">{application.note || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        {onRemove ? (
                          <button
                            type="button"
                            aria-label={`Remover ${application.brand} ${application.model} de ${product?.name ?? application.productId}`}
                            disabled={busy}
                            data-action="remover-aplicacao"
                            onClick={() => onRemove(application.id)}
                            className="rounded-pill p-2 text-muted hover:bg-surface hover:text-danger disabled:opacity-40"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden focusable={false} />
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

export function Aplicacoes(): ReactElement {
  const query = useApplications();
  const { add, remove } = useApplicationActions();
  return (
    <CatalogBoundary>
      {(catalog: CatalogDataset) => {
        if (query.isPending) return <StatusPanel kind="loading" title="Carregando aplicações…" />;
        if (query.error) return <StatusPanel kind="error" title="Não foi possível carregar as aplicações" detail={errorDetail(query.error)} />;
        const failure = add.error ?? remove.error;
        return (
          <AplicacoesView
            applications={query.data ?? null}
            products={catalog.products.map((product) => ({ productId: product.productId, name: product.name, sku: product.sku }))}
            onAdd={(input) => add.mutate(input)}
            onRemove={(id) => remove.mutate(id)}
            busy={add.isPending || remove.isPending}
            error={failure ? `Não foi possível concluir: ${failure.message}` : null}
          />
        );
      }}
    </CatalogBoundary>
  );
}
